import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  Chat,
  Hub,
  Refresh,
  Save,
  Send,
  SettingsInputAntenna,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { enumLabel } from '../i18n/translate';
import { useAuth } from '../contexts/AuthContext';
import { whatsappApi } from '../services/api';
import { formatDateTime as formatAppDateTime } from '../utils/formatters';
import {
  WhatsAppConnectionCheckResponse,
  WhatsAppEmbeddedSignupConfig,
  WhatsAppInboundMessageLog,
  WhatsAppLogSummaryResponse,
  WhatsAppOutboundMessageLog,
  WhatsAppPlatformConfig,
  WhatsAppPlatformConfigRequest,
  WhatsAppSettings,
  WhatsAppSettingsOverview,
  WhatsAppSettingsRequest,
  WhatsAppTestMessageResponse,
  WhatsAppWebhookEventLog,
} from '../types/whatsapp';

type TFunction = (key: string, defaultValue?: string, options?: Record<string, unknown>) => string;

interface ManualConnectionFormState {
  id?: number;
  enabled: boolean;
  connectionName: string;
  defaultConnection: boolean;
  businessDisplayName: string;
  metaBusinessId: string;
  whatsappBusinessAccountId: string;
  phoneNumberId: string;
  accessToken: string;
  twoStepVerificationPin: string;
}

interface PlatformFormState {
  enabled: boolean;
  metaAppId: string;
  facebookLoginConfigurationId: string;
  graphApiVersion: string;
  webhookCallbackUrl: string;
  webhookVerifyToken: string;
  appSecret: string;
}

interface EmbeddedSignupEventPayload {
  eventType?: string;
  businessDisplayName?: string;
  businessId?: string;
  whatsappBusinessAccountId?: string;
  phoneNumberId?: string;
  verifiedPhoneNumber?: string;
  verifiedName?: string;
  rawPayloadJson?: string;
}

interface EmbeddedSignupFlowState {
  code?: string;
  eventPayload?: EmbeddedSignupEventPayload | null;
  submitting: boolean;
}

const emptyManualFormState: ManualConnectionFormState = {
  enabled: true,
  connectionName: '',
  defaultConnection: false,
  businessDisplayName: '',
  metaBusinessId: '',
  whatsappBusinessAccountId: '',
  phoneNumberId: '',
  accessToken: '',
  twoStepVerificationPin: '',
};

const emptyPlatformFormState: PlatformFormState = {
  enabled: true,
  metaAppId: '',
  facebookLoginConfigurationId: '',
  graphApiVersion: 'v25.0',
  webhookCallbackUrl: '',
  webhookVerifyToken: '',
  appSecret: '',
};

const emptyLogs: WhatsAppLogSummaryResponse = {
  outboundMessages: [],
  inboundMessages: [],
  webhookEvents: [],
};

const cardSx = {
  p: 3,
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 'none',
};

const e164Pattern = /^\+[1-9]\d{1,14}$/;

let facebookSdkPromise: Promise<void> | null = null;

function ensureFacebookSdkLoaded(): Promise<void> {
  if (window.FB) {
    return Promise.resolve();
  }

  if (facebookSdkPromise) {
    return facebookSdkPromise;
  }

  facebookSdkPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById('facebook-jssdk') as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load the Facebook SDK.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.async = true;
    script.defer = true;
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load the Facebook SDK.'));
    document.body.appendChild(script);
  });

  return facebookSdkPromise;
}

function formatDateTime(value: string | undefined, t: TFunction) {
  if (!value) {
    return t('whatsapp.common.notAvailable', 'Not available');
  }
  return formatAppDateTime(value);
}

function connectionChipColor(status?: string): 'success' | 'error' | 'warning' | 'default' {
  switch ((status || '').toUpperCase()) {
    case 'CONNECTED':
    case 'DELIVERED':
    case 'READ':
    case 'SENT':
    case 'COMPLETED':
      return 'success';
    case 'DISCONNECTED':
    case 'ERROR':
    case 'FAILED':
      return 'error';
    case 'NOT_CONFIGURED':
    case 'PENDING':
    case 'DRAFT':
      return 'warning';
    default:
      return 'default';
  }
}

function humanizeStatus(value: string | undefined, t: TFunction) {
  if (!value) {
    return t('whatsapp.common.unknown', 'Unknown');
  }

  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function parseEmbeddedSignupMessage(rawData: unknown): EmbeddedSignupEventPayload | null {
  let parsed = rawData;

  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== 'object') {
    return null;
  }

  const root = parsed as Record<string, any>;
  if (root.type !== 'WA_EMBEDDED_SIGNUP') {
    return null;
  }

  const data = root.data && typeof root.data === 'object' ? root.data : {};
  return {
    eventType: typeof root.event === 'string' ? root.event : undefined,
    businessDisplayName: typeof data.business_display_name === 'string' ? data.business_display_name : undefined,
    businessId: typeof data.business_id === 'string' ? data.business_id : undefined,
    whatsappBusinessAccountId: typeof data.waba_id === 'string' ? data.waba_id : undefined,
    phoneNumberId: typeof data.phone_number_id === 'string' ? data.phone_number_id : undefined,
    verifiedPhoneNumber:
      typeof data.display_phone_number === 'string'
        ? data.display_phone_number
        : typeof data.phone_number === 'string'
          ? data.phone_number
          : undefined,
    verifiedName: typeof data.verified_name === 'string' ? data.verified_name : undefined,
    rawPayloadJson: JSON.stringify(root),
  };
}

function JsonDetails({ label, value }: { label: string; value?: string }) {
  if (!value) {
    return null;
  }

  return (
    <Box component="details" sx={{ mt: 1.5 }}>
      <Box component="summary" sx={{ cursor: 'pointer', fontWeight: 600 }}>
        {label}
      </Box>
      <Box
        component="pre"
        sx={{
          mt: 1,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: 'grey.100',
          fontSize: '0.75rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowX: 'auto',
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

function ConnectionMeta({
  connectionName,
  phoneNumberId,
  t,
}: {
  connectionName?: string;
  phoneNumberId?: string;
  t: TFunction;
}) {
  if (!connectionName && !phoneNumberId) {
    return null;
  }

  return (
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
      {connectionName || t('whatsapp.common.connection', 'Connection')}
      {phoneNumberId ? ` • ${phoneNumberId}` : ''}
    </Typography>
  );
}

function OutboundLogs({ logs, t }: { logs: WhatsAppOutboundMessageLog[]; t: TFunction }) {
  if (!logs.length) {
    return (
      <Typography color="text.secondary">
        {t('whatsapp.empty.outbound', 'No outbound WhatsApp messages yet.')}
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {logs.map((log) => (
        <Paper key={log.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {log.destinationPhoneNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(log.createdAt, t)}
              </Typography>
              <ConnectionMeta connectionName={log.connectionName} phoneNumberId={log.phoneNumberId} t={t} />
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={enumLabel(t, 'whatsapp.deliveryStatus', log.deliveryStatus)} color={connectionChipColor(log.deliveryStatus)} size="small" />
              {log.testMessage && <Chip label={t('whatsapp.common.test', 'Test')} variant="outlined" size="small" />}
            </Stack>
          </Stack>

          <Stack spacing={0.75} sx={{ mt: 1.5 }}>
            <Typography variant="body2">
              <strong>{t('whatsapp.logs.template', 'Template:')}</strong> {log.templateName || t('whatsapp.common.na', 'N/A')} {log.templateLanguage ? `(${log.templateLanguage})` : ''}
            </Typography>
            <Typography variant="body2">
              <strong>{t('whatsapp.logs.messageId', 'WhatsApp Message ID:')}</strong> {log.metaMessageId || t('whatsapp.common.pending', 'Pending')}
            </Typography>
            {log.errorMessage && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {log.errorMessage}
              </Alert>
            )}
          </Stack>

          <JsonDetails label={t('whatsapp.payload.request', 'Request payload')} value={log.requestPayloadJson} />
          <JsonDetails label={t('whatsapp.payload.response', 'Response payload')} value={log.responsePayloadJson} />
          <JsonDetails label={t('whatsapp.payload.error', 'Error payload')} value={log.errorPayloadJson} />
        </Paper>
      ))}
    </Stack>
  );
}

function InboundLogs({ logs, t }: { logs: WhatsAppInboundMessageLog[]; t: TFunction }) {
  if (!logs.length) {
    return (
      <Typography color="text.secondary">
        {t('whatsapp.empty.inbound', 'No inbound WhatsApp messages received yet.')}
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {logs.map((log) => (
        <Paper key={log.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {log.contactName || log.fromPhoneNumber || t('whatsapp.common.unknownSender', 'Unknown sender')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(log.receivedAt, t)}
              </Typography>
              <ConnectionMeta connectionName={log.connectionName} phoneNumberId={log.phoneNumberId} t={t} />
            </Box>
            <Chip label={enumLabel(t, 'whatsapp.messageType', log.messageType)} size="small" />
          </Stack>

          <Stack spacing={0.75} sx={{ mt: 1.5 }}>
            <Typography variant="body2">
              <strong>{t('whatsapp.logs.from', 'From:')}</strong> {log.fromPhoneNumber || t('whatsapp.common.na', 'N/A')}
            </Typography>
            <Typography variant="body2">
              <strong>{t('whatsapp.logs.waId', 'WA ID:')}</strong> {log.contactWaId || t('whatsapp.common.na', 'N/A')}
            </Typography>
            <Typography variant="body2">
              <strong>{t('whatsapp.logs.message', 'Message:')}</strong> {log.messageText || t('whatsapp.common.noTextPayload', 'No text payload')}
            </Typography>
          </Stack>

          <JsonDetails label={t('whatsapp.payload.raw', 'Raw payload')} value={log.rawPayloadJson} />
        </Paper>
      ))}
    </Stack>
  );
}

function WebhookLogs({ logs, t }: { logs: WhatsAppWebhookEventLog[]; t: TFunction }) {
  if (!logs.length) {
    return (
      <Typography color="text.secondary">
        {t('whatsapp.empty.webhook', 'No webhook events logged yet.')}
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {logs.map((log) => (
        <Paper key={log.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {humanizeStatus(log.eventType, t)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(log.receivedAt, t)}
              </Typography>
              <ConnectionMeta connectionName={log.connectionName} phoneNumberId={log.phoneNumberId} t={t} />
            </Box>
            <Chip
              label={log.processed ? t('whatsapp.common.processed', 'Processed') : t('whatsapp.common.needsReview', 'Needs review')}
              color={log.processed ? 'success' : 'warning'}
              size="small"
            />
          </Stack>

          <Stack spacing={0.75} sx={{ mt: 1.5 }}>
            <Typography variant="body2">
              <strong>{t('whatsapp.logs.summary', 'Summary:')}</strong> {log.eventSummary || t('whatsapp.common.noSummary', 'No summary')}
            </Typography>
            {log.processingError && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {log.processingError}
              </Alert>
            )}
          </Stack>

          <JsonDetails label={t('whatsapp.payload.webhook', 'Webhook payload')} value={log.payloadJson} />
        </Paper>
      ))}
    </Stack>
  );
}

function mapConnectionToForm(connection?: WhatsAppSettings | null): ManualConnectionFormState {
  if (!connection) {
    return emptyManualFormState;
  }

  return {
    id: connection.id,
    enabled: connection.enabled ?? true,
    connectionName: connection.connectionName || '',
    defaultConnection: connection.defaultConnection ?? false,
    businessDisplayName: connection.businessDisplayName || '',
    metaBusinessId: connection.metaBusinessId || '',
    whatsappBusinessAccountId: connection.whatsappBusinessAccountId || '',
    phoneNumberId: connection.phoneNumberId || '',
    accessToken: '',
    twoStepVerificationPin: '',
  };
}

function mapPlatformToForm(config?: WhatsAppPlatformConfig | null): PlatformFormState {
  if (!config) {
    return emptyPlatformFormState;
  }

  return {
    enabled: config.enabled ?? true,
    metaAppId: config.metaAppId || '',
    facebookLoginConfigurationId: config.facebookLoginConfigurationId || '',
    graphApiVersion: config.graphApiVersion || 'v25.0',
    webhookCallbackUrl: config.webhookCallbackUrl || '',
    webhookVerifyToken: config.webhookVerifyToken || '',
    appSecret: '',
  };
}

export const WhatsAppSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isSuperuser = user?.role === 'SUPERUSER';
  const [selectedConnectionId, setSelectedConnectionId] = useState<number | null>(null);
  const [isCreatingNewConnection, setIsCreatingNewConnection] = useState(false);
  const [manualFormData, setManualFormData] = useState<ManualConnectionFormState>(emptyManualFormState);
  const [platformFormData, setPlatformFormData] = useState<PlatformFormState>(emptyPlatformFormState);
  const [isManualDirty, setIsManualDirty] = useState(false);
  const [isPlatformDirty, setIsPlatformDirty] = useState(false);
  const [destinationPhoneNumber, setDestinationPhoneNumber] = useState('');
  const [latestConnectionCheck, setLatestConnectionCheck] = useState<WhatsAppConnectionCheckResponse | null>(null);
  const [latestTestMessage, setLatestTestMessage] = useState<WhatsAppTestMessageResponse | null>(null);
  const signupFlowRef = useRef<EmbeddedSignupFlowState>({ submitting: false });

  const overviewQuery = useQuery(['whatsappOverview'], () => whatsappApi.getOverview());
  const platformQuery = useQuery(['whatsappPlatformConfig'], () => whatsappApi.getPlatformConfig(), {
    enabled: isSuperuser,
  });
  const logsQuery = useQuery(
    ['whatsappLogs', selectedConnectionId],
    () => whatsappApi.getLogs(20, selectedConnectionId || undefined),
    {
      enabled: Boolean(overviewQuery.data?.hasCompanyContext),
    }
  );

  const overview = overviewQuery.data as WhatsAppSettingsOverview | undefined;
  const embeddedSignupConfig = overview?.embeddedSignupConfig as WhatsAppEmbeddedSignupConfig | undefined;
  const connections = useMemo(() => overview?.connections || [], [overview?.connections]);

  const activeConnection = useMemo(() => {
    if (!selectedConnectionId) {
      return undefined;
    }
    return connections.find((connection) => connection.id === selectedConnectionId);
  }, [connections, selectedConnectionId]);

  const activeLogs = useMemo<WhatsAppLogSummaryResponse>(() => logsQuery.data || emptyLogs, [logsQuery.data]);
  const destinationNumberValid = !destinationPhoneNumber || e164Pattern.test(destinationPhoneNumber.trim());

  useEffect(() => {
    if (!connections.length) {
      if (!isCreatingNewConnection) {
        setSelectedConnectionId(null);
        setManualFormData(emptyManualFormState);
      }
      return;
    }

    const selectedStillExists = selectedConnectionId
      ? connections.some((connection) => connection.id === selectedConnectionId)
      : false;

    if (!selectedStillExists && !isCreatingNewConnection) {
      const nextConnectionId = overview?.defaultConnectionId || connections[0].id || null;
      setSelectedConnectionId(nextConnectionId ?? null);
    }
  }, [connections, overview?.defaultConnectionId, selectedConnectionId, isCreatingNewConnection]);

  useEffect(() => {
    if (!activeConnection || isCreatingNewConnection) {
      return;
    }

    setManualFormData(mapConnectionToForm(activeConnection));
    setIsManualDirty(false);
  }, [activeConnection, isCreatingNewConnection]);

  useEffect(() => {
    if (!platformQuery.data) {
      return;
    }

    setPlatformFormData(mapPlatformToForm(platformQuery.data));
    setIsPlatformDirty(false);
  }, [platformQuery.data]);

  useEffect(() => {
    if (!embeddedSignupConfig?.ready || !embeddedSignupConfig.metaAppId || !embeddedSignupConfig.graphApiVersion) {
      return;
    }

    ensureFacebookSdkLoaded()
      .then(() => {
        window.FB?.init({
          appId: embeddedSignupConfig.metaAppId!,
          autoLogAppEvents: true,
          xfbml: false,
          version: embeddedSignupConfig.graphApiVersion!,
        });
      })
      .catch((error) => {
        toast.error(t('whatsapp.toast.facebookSdkFailed', 'Failed to load the Facebook SDK.'));
      });
  }, [embeddedSignupConfig?.ready, embeddedSignupConfig?.metaAppId, embeddedSignupConfig?.graphApiVersion, t]);

  const refreshPageData = async () => {
    const tasks: Promise<unknown>[] = [overviewQuery.refetch()];
    if (isSuperuser) {
      tasks.push(platformQuery.refetch());
    }
    if (overviewQuery.data?.hasCompanyContext) {
      tasks.push(logsQuery.refetch());
    }
    await Promise.all(tasks);
    toast.success(t('whatsapp.toast.pageRefreshed', 'WhatsApp settings refreshed.'));
  };

  const savePlatformMutation = useMutation(
    (payload: WhatsAppPlatformConfigRequest) => whatsappApi.updatePlatformConfig(payload),
    {
      onSuccess: (config) => {
        queryClient.setQueryData(['whatsappPlatformConfig'], config);
        queryClient.invalidateQueries(['whatsappOverview']);
        setPlatformFormData(mapPlatformToForm(config));
        setIsPlatformDirty(false);
        toast.success(t('whatsapp.toast.platformSaved', 'Shared Meta platform settings saved.'));
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('whatsapp.toast.platformSaveFailed', 'Failed to save the Meta platform settings.'));
      },
    }
  );

  const saveConnectionMutation = useMutation(
    (payload: WhatsAppSettingsRequest) => whatsappApi.updateSettings(payload),
    {
      onSuccess: (connection) => {
        queryClient.invalidateQueries(['whatsappOverview']);
        setSelectedConnectionId(connection.id || null);
        setIsCreatingNewConnection(false);
        setManualFormData(mapConnectionToForm(connection));
        setIsManualDirty(false);
        toast.success(t('whatsapp.toast.settingsSaved', 'WhatsApp connection saved.'));
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('whatsapp.toast.saveFailed', 'Failed to save the WhatsApp connection.'));
      },
    }
  );

  const connectionCheckMutation = useMutation(
    (mode: { connectionId?: number; action: 'test' | 'refresh' }) =>
      mode.connectionId
        ? mode.action === 'test'
          ? whatsappApi.testConnectionById(mode.connectionId)
          : whatsappApi.refreshStatusById(mode.connectionId)
        : mode.action === 'test'
          ? whatsappApi.testConnection()
          : whatsappApi.refreshStatus(),
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries(['whatsappOverview']);
        setLatestConnectionCheck(result);
        toast.success(
          result.success
            ? t('whatsapp.toast.connectionCheckSucceeded', 'WhatsApp connection check succeeded.')
            : t('whatsapp.toast.connectionCheckIssues', 'WhatsApp connection check completed with issues.')
        );
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('whatsapp.toast.connectionCheckFailed', 'Failed to check the WhatsApp connection.'));
      },
    }
  );

  const setDefaultConnectionMutation = useMutation(
    (connectionId: number) => whatsappApi.setDefaultConnection(connectionId),
    {
      onSuccess: (connection) => {
        queryClient.invalidateQueries(['whatsappOverview']);
        setSelectedConnectionId(connection.id || null);
        toast.success(t('whatsapp.toast.defaultConnectionSet', 'Default WhatsApp connection updated.'));
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('whatsapp.toast.defaultConnectionFailed', 'Failed to update the default WhatsApp connection.'));
      },
    }
  );

  const completeEmbeddedSignupMutation = useMutation(
    (payload: {
      code: string;
      eventPayload: EmbeddedSignupEventPayload;
    }) =>
      whatsappApi.completeEmbeddedSignup({
        code: payload.code,
        eventType: payload.eventPayload.eventType,
        businessDisplayName: payload.eventPayload.businessDisplayName,
        businessId: payload.eventPayload.businessId,
        whatsappBusinessAccountId: payload.eventPayload.whatsappBusinessAccountId,
        phoneNumberId: payload.eventPayload.phoneNumberId,
        verifiedPhoneNumber: payload.eventPayload.verifiedPhoneNumber,
        verifiedName: payload.eventPayload.verifiedName,
        defaultConnection: !connections.length,
        rawEventPayloadJson: payload.eventPayload.rawPayloadJson,
      }),
    {
      onSuccess: (result) => {
        signupFlowRef.current = { submitting: false };
        queryClient.invalidateQueries(['whatsappOverview']);
        queryClient.invalidateQueries(['whatsappLogs']);
        setLatestConnectionCheck(result);
        setSelectedConnectionId(result.settings.id || null);
        setIsCreatingNewConnection(false);
        toast.success(
          result.success
            ? t('whatsapp.toast.embeddedSignupCompleted', 'WhatsApp Embedded Signup completed successfully.')
            : t('whatsapp.toast.embeddedSignupCompletedWithWarnings', 'Embedded Signup finished, but the connection still needs attention.')
        );
      },
      onError: (error: any) => {
        signupFlowRef.current = { submitting: false };
        toast.error(error.response?.data?.error || t('whatsapp.toast.embeddedSignupFailed', 'Failed to finish WhatsApp Embedded Signup.'));
      },
    }
  );

  const sendTestMessageMutation = useMutation(
    (payload: { connectionId?: number; destinationPhoneNumber: string }) => whatsappApi.sendTestMessage(payload),
    {
      onSuccess: (result) => {
        setLatestTestMessage(result);
        queryClient.invalidateQueries(['whatsappLogs']);
        queryClient.invalidateQueries(['whatsappOverview']);
        toast.success(
          result.success
            ? t('whatsapp.toast.testMessageSent', 'Test WhatsApp message sent.')
            : t('whatsapp.toast.testMessageRejected', 'Meta rejected the test message.')
        );
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('whatsapp.toast.testMessageFailed', 'Failed to send the WhatsApp test message.'));
      },
    }
  );

  useEffect(() => {
    const onWindowMessage = (event: MessageEvent) => {
      const isTrustedMetaOrigin =
        event.origin === 'https://www.facebook.com' ||
        event.origin === 'https://web.facebook.com' ||
        event.origin.endsWith('.facebook.com');

      if (!isTrustedMetaOrigin) {
        return;
      }

      const payload = parseEmbeddedSignupMessage(event.data);
      if (!payload) {
        return;
      }

      if ((payload.eventType || '').toUpperCase() === 'CANCEL') {
        signupFlowRef.current = { submitting: false };
        toast.error(t('whatsapp.toast.embeddedSignupCancelled', 'WhatsApp Embedded Signup was cancelled.'));
        return;
      }

      if ((payload.eventType || '').toUpperCase().startsWith('FINISH')) {
        signupFlowRef.current.eventPayload = payload;
        if (signupFlowRef.current.code && !signupFlowRef.current.submitting) {
          signupFlowRef.current.submitting = true;
          completeEmbeddedSignupMutation.mutate({
            code: signupFlowRef.current.code,
            eventPayload: payload,
          });
        }
      }
    };

    window.addEventListener('message', onWindowMessage);
    return () => window.removeEventListener('message', onWindowMessage);
  }, [completeEmbeddedSignupMutation, t]);

  const handlePlatformFieldChange = (field: keyof PlatformFormState, value: string | boolean) => {
    setPlatformFormData((current) => ({
      ...current,
      [field]: value,
    }));
    setIsPlatformDirty(true);
  };

  const handleManualFieldChange = (field: keyof ManualConnectionFormState, value: string | boolean) => {
    setManualFormData((current) => ({
      ...current,
      [field]: value,
    }));
    setIsManualDirty(true);
  };

  const handleCreateNewConnection = () => {
    setIsCreatingNewConnection(true);
    setSelectedConnectionId(null);
    setManualFormData({
      ...emptyManualFormState,
      defaultConnection: !connections.length,
    });
    setIsManualDirty(false);
  };

  const handleSelectConnection = (connection: WhatsAppSettings) => {
    setSelectedConnectionId(connection.id || null);
    setIsCreatingNewConnection(false);
    setManualFormData(mapConnectionToForm(connection));
    setIsManualDirty(false);
  };

  const handleSavePlatform = () => {
    savePlatformMutation.mutate({
      enabled: platformFormData.enabled,
      metaAppId: platformFormData.metaAppId.trim() || undefined,
      facebookLoginConfigurationId: platformFormData.facebookLoginConfigurationId.trim() || undefined,
      graphApiVersion: platformFormData.graphApiVersion.trim() || undefined,
      webhookCallbackUrl: platformFormData.webhookCallbackUrl.trim() || undefined,
      webhookVerifyToken: platformFormData.webhookVerifyToken.trim() || undefined,
      appSecret: platformFormData.appSecret.trim() || undefined,
    });
  };

  const handleSaveConnection = () => {
    saveConnectionMutation.mutate({
      id: isCreatingNewConnection ? undefined : manualFormData.id,
      enabled: manualFormData.enabled,
      connectionName: manualFormData.connectionName.trim() || undefined,
      defaultConnection: manualFormData.defaultConnection,
      businessDisplayName: manualFormData.businessDisplayName.trim() || undefined,
      metaBusinessId: manualFormData.metaBusinessId.trim() || undefined,
      whatsappBusinessAccountId: manualFormData.whatsappBusinessAccountId.trim() || undefined,
      phoneNumberId: manualFormData.phoneNumberId.trim() || undefined,
      accessToken: manualFormData.accessToken.trim() || undefined,
      twoStepVerificationPin: manualFormData.twoStepVerificationPin.trim() || undefined,
    });
  };

  const handleLaunchEmbeddedSignup = async () => {
    if (!embeddedSignupConfig?.ready || !embeddedSignupConfig.facebookLoginConfigurationId) {
      toast.error(
        embeddedSignupConfig?.message ||
          t('whatsapp.toast.embeddedSignupNotReady', 'Complete the shared Meta platform settings first.')
      );
      return;
    }

    try {
      await ensureFacebookSdkLoaded();
      window.FB?.init({
        appId: embeddedSignupConfig.metaAppId || '',
        autoLogAppEvents: true,
        xfbml: false,
        version: embeddedSignupConfig.graphApiVersion || 'v25.0',
      });
    } catch (error: any) {
      toast.error(t('whatsapp.toast.facebookSdkFailed', 'Failed to load the Facebook SDK.'));
      return;
    }

    if (!window.FB) {
      toast.error(t('whatsapp.toast.facebookSdkMissing', 'The Facebook SDK is not available.'));
      return;
    }

    signupFlowRef.current = { submitting: false };
    window.FB.login(
      (response) => {
        const code = response.authResponse?.code;
        if (!code) {
          toast.error(t('whatsapp.toast.embeddedSignupCodeMissing', 'Meta did not return an authorization code.'));
          return;
        }

        signupFlowRef.current.code = code;
        if (signupFlowRef.current.eventPayload && !signupFlowRef.current.submitting) {
          signupFlowRef.current.submitting = true;
          completeEmbeddedSignupMutation.mutate({
            code,
            eventPayload: signupFlowRef.current.eventPayload,
          });
        }
      },
      {
        config_id: embeddedSignupConfig.facebookLoginConfigurationId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
        },
      }
    );
  };

  const handleSendTestMessage = () => {
    if (!destinationNumberValid) {
      toast.error(t('whatsapp.toast.invalidPhoneFormat', 'Enter the destination phone number in E.164 format, for example +15551234567.'));
      return;
    }

    sendTestMessageMutation.mutate({
      connectionId: selectedConnectionId || undefined,
      destinationPhoneNumber: destinationPhoneNumber.trim(),
    });
  };

  if (overviewQuery.isLoading || (isSuperuser && platformQuery.isLoading)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Paper
        sx={{
          ...cardSx,
          mb: 3,
          p: { xs: 3, md: 4 },
          background: 'linear-gradient(135deg, #f5fff8 0%, #ebfaf1 100%)',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
        >
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Chat color="success" />
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                {t('nav.whatsappSettings', { defaultValue: 'WhatsApp Settings' })}
              </Typography>
            </Stack>
            <Typography variant="body1" color="text.secondary">
              {t('whatsapp.subtitle.multiTenant', {
                defaultValue:
                  'Run one shared Meta app for the whole platform, let each company connect one or more WhatsApp numbers, and keep message logs per connection.',
              })}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={overviewQuery.isFetching || platformQuery.isFetching || logsQuery.isFetching ? <CircularProgress size={18} /> : <Refresh />}
            onClick={refreshPageData}
            disabled={overviewQuery.isFetching || platformQuery.isFetching || logsQuery.isFetching}
          >
            {t('common.refresh', { defaultValue: 'Refresh' })}
          </Button>
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        {isSuperuser && (
          <Grid item xs={12}>
            <Paper sx={cardSx}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Hub color="primary" />
                <Box>
                  <Typography variant="h6">{t('whatsapp.sections.platform.title', 'Shared Meta Platform Configuration')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      'whatsapp.sections.platform.description',
                      'This is the one Meta app and Facebook Login for Business configuration that all tenants will use for Embedded Signup and the shared webhook.'
                    )}
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={2.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={platformFormData.enabled}
                      onChange={(event) => handlePlatformFieldChange('enabled', event.target.checked)}
                    />
                  }
                  label={t('whatsapp.fields.enabled', 'Enabled')}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('whatsapp.fields.metaAppId', 'Meta App ID')}
                      value={platformFormData.metaAppId}
                      onChange={(event) => handlePlatformFieldChange('metaAppId', event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('whatsapp.fields.facebookLoginConfigurationId', 'Facebook Login Configuration ID')}
                      value={platformFormData.facebookLoginConfigurationId}
                      onChange={(event) => handlePlatformFieldChange('facebookLoginConfigurationId', event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('whatsapp.fields.graphApiVersion', 'Graph API Version')}
                      value={platformFormData.graphApiVersion}
                      onChange={(event) => handlePlatformFieldChange('graphApiVersion', event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('whatsapp.fields.webhookVerifyToken', 'Webhook Verify Token')}
                      value={platformFormData.webhookVerifyToken}
                      onChange={(event) => handlePlatformFieldChange('webhookVerifyToken', event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('whatsapp.fields.webhookCallbackUrl', 'Webhook Callback URL')}
                      value={platformFormData.webhookCallbackUrl}
                      onChange={(event) => handlePlatformFieldChange('webhookCallbackUrl', event.target.value)}
                      helperText={t(
                        'whatsapp.fields.webhookCallbackUrlHelper',
                        'Use this exact HTTPS callback URL in Meta when configuring the shared webhook.'
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="password"
                      label={t('whatsapp.fields.appSecret', 'Meta App Secret')}
                      value={platformFormData.appSecret}
                      onChange={(event) => handlePlatformFieldChange('appSecret', event.target.value)}
                      helperText={
                        platformQuery.data?.hasAppSecret
                          ? t('whatsapp.fields.appSecretHelperExisting', 'A Meta app secret is already stored securely. Leave this blank to keep it unchanged.')
                          : t('whatsapp.fields.appSecretHelperNew', 'Store the Meta app secret used for webhook signature validation and Embedded Signup code exchange.')
                      }
                    />
                  </Grid>
                </Grid>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    variant="contained"
                    startIcon={savePlatformMutation.isLoading ? <CircularProgress size={18} /> : <Save />}
                    onClick={handleSavePlatform}
                    disabled={!isPlatformDirty || savePlatformMutation.isLoading}
                  >
                    {t('common.saveChanges', { defaultValue: 'Save Changes' })}
                  </Button>
                  <Chip
                    label={
                      platformQuery.data?.embeddedSignupReady
                        ? t('whatsapp.status.embeddedSignupReady', 'Embedded Signup Ready')
                        : t('whatsapp.status.embeddedSignupNeedsSetup', 'Embedded Signup Needs Setup')
                    }
                    color={platformQuery.data?.embeddedSignupReady ? 'success' : 'warning'}
                  />
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} lg={7}>
          <Stack spacing={3}>
            <Paper sx={cardSx}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <SettingsInputAntenna color="success" />
                <Box>
                  <Typography variant="h6">{t('whatsapp.sections.connections.title', 'Company Connections')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(
                      'whatsapp.sections.connections.description',
                      'Each company can keep multiple WhatsApp numbers. One connection stays marked as the default sender.'
                    )}
                  </Typography>
                </Box>
              </Stack>

              {!overview?.hasCompanyContext ? (
                <Alert severity="info">
                  {t(
                    'whatsapp.alerts.superuserNoCompany',
                    'This SUPERUSER account is not linked to a company, so you can manage the shared Meta configuration here but not tenant-specific WhatsApp connections.'
                  )}
                </Alert>
              ) : (
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                      variant="contained"
                      startIcon={completeEmbeddedSignupMutation.isLoading ? <CircularProgress size={18} /> : <Hub />}
                      onClick={handleLaunchEmbeddedSignup}
                      disabled={!embeddedSignupConfig?.ready || completeEmbeddedSignupMutation.isLoading}
                    >
                      {t('whatsapp.buttons.connectWithMeta', 'Connect With Meta')}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={handleCreateNewConnection}
                    >
                      {t('whatsapp.buttons.newManualConnection', 'New Manual Connection')}
                    </Button>
                  </Stack>

                  {embeddedSignupConfig && !embeddedSignupConfig.ready && (
                    <Alert severity="warning">
                      {embeddedSignupConfig.message || t('whatsapp.alerts.embeddedSignupNotReady', 'Embedded Signup is not ready yet.')}
                    </Alert>
                  )}

                  {connections.length ? (
                    <Stack spacing={1.5}>
                      {connections.map((connection) => {
                        const selected = connection.id === selectedConnectionId && !isCreatingNewConnection;
                        return (
                          <Paper
                            key={connection.id}
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              borderColor: selected ? 'success.main' : 'divider',
                              backgroundColor: selected ? 'success.50' : 'background.paper',
                            }}
                          >
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              spacing={1.5}
                              justifyContent="space-between"
                              alignItems={{ xs: 'flex-start', md: 'center' }}
                            >
                              <Box>
                                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    {connection.connectionName || connection.businessDisplayName || t('whatsapp.common.connection', 'Connection')}
                                  </Typography>
                                  {connection.defaultConnection && (
                                    <Chip size="small" color="success" label={t('whatsapp.common.default', 'Default')} />
                                  )}
                                  <Chip
                                    size="small"
                                    color={connectionChipColor(connection.connectionStatus)}
                                    label={enumLabel(t, 'whatsapp.connectionStatus', connection.connectionStatus)}
                                  />
                                  {connection.onboardingMethod && (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={enumLabel(t, 'whatsapp.onboardingMethod', connection.onboardingMethod)}
                                    />
                                  )}
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                  {connection.phoneNumberId || t('whatsapp.common.noPhoneNumberId', 'No phone number ID yet')}
                                  {connection.whatsappBusinessAccountId ? ` • WABA ${connection.whatsappBusinessAccountId}` : ''}
                                </Typography>
                                {connection.lastErrorMessage && (
                                  <Typography variant="body2" color="error.main" sx={{ mt: 0.75 }}>
                                    {connection.lastErrorMessage}
                                  </Typography>
                                )}
                              </Box>

                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Button variant={selected ? 'contained' : 'outlined'} onClick={() => handleSelectConnection(connection)}>
                                  {selected ? t('whatsapp.buttons.selected', 'Selected') : t('whatsapp.buttons.edit', 'Edit')}
                                </Button>
                                <Button
                                  variant="outlined"
                                  onClick={() => setDefaultConnectionMutation.mutate(connection.id!)}
                                  disabled={Boolean(connection.defaultConnection) || setDefaultConnectionMutation.isLoading}
                                >
                                  {t('whatsapp.buttons.makeDefault', 'Make Default')}
                                </Button>
                              </Stack>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Alert severity="info">
                      {t(
                        'whatsapp.alerts.noConnections',
                        'No WhatsApp connections have been added for this company yet. Start with Embedded Signup or create one manually below.'
                      )}
                    </Alert>
                  )}
                </Stack>
              )}
            </Paper>

            {overview?.hasCompanyContext && (
              <Paper sx={cardSx}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <Save color="success" />
                  <Box>
                    <Typography variant="h6">
                      {isCreatingNewConnection
                        ? t('whatsapp.sections.manualEditor.newTitle', 'New Manual Connection')
                        : t('whatsapp.sections.manualEditor.title', 'Connection Editor')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        'whatsapp.sections.manualEditor.description',
                        'Use this as a fallback when you already have the WABA ID, Phone Number ID, and a business token from Meta.'
                      )}
                    </Typography>
                  </Box>
                </Stack>

                <Stack spacing={2.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={manualFormData.enabled}
                        onChange={(event) => handleManualFieldChange('enabled', event.target.checked)}
                      />
                    }
                    label={t('whatsapp.fields.enabled', 'Enabled')}
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('whatsapp.fields.connectionName', 'Connection Name')}
                        value={manualFormData.connectionName}
                        onChange={(event) => handleManualFieldChange('connectionName', event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('whatsapp.fields.businessDisplayName', 'Business Display Name')}
                        value={manualFormData.businessDisplayName}
                        onChange={(event) => handleManualFieldChange('businessDisplayName', event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('whatsapp.fields.metaBusinessId', 'Meta Business ID')}
                        value={manualFormData.metaBusinessId}
                        onChange={(event) => handleManualFieldChange('metaBusinessId', event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('whatsapp.fields.whatsappBusinessAccountId', 'WhatsApp Business Account ID')}
                        value={manualFormData.whatsappBusinessAccountId}
                        onChange={(event) => handleManualFieldChange('whatsappBusinessAccountId', event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('whatsapp.fields.phoneNumberId', 'Phone Number ID')}
                        value={manualFormData.phoneNumberId}
                        onChange={(event) => handleManualFieldChange('phoneNumberId', event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="password"
                        label={t('whatsapp.fields.twoStepVerificationPin', 'Two-Step Verification PIN')}
                        value={manualFormData.twoStepVerificationPin}
                        onChange={(event) => handleManualFieldChange('twoStepVerificationPin', event.target.value)}
                        helperText={
                          activeConnection?.hasTwoStepVerificationPin
                            ? t('whatsapp.fields.twoStepVerificationPinHelperExisting', 'A PIN is already stored securely. Leave this blank to keep it unchanged.')
                            : t('whatsapp.fields.twoStepVerificationPinHelperNew', 'Optional 6-digit PIN used when Meta asks to register the number.')
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="password"
                        label={t('whatsapp.fields.accessToken', 'Access Token')}
                        value={manualFormData.accessToken}
                        onChange={(event) => handleManualFieldChange('accessToken', event.target.value)}
                        helperText={
                          activeConnection?.hasAccessToken
                            ? t('whatsapp.fields.accessTokenHelperExisting', 'A token is already stored securely. Leave this blank to keep it unchanged.')
                            : t('whatsapp.fields.accessTokenHelperNew', 'Paste the business token Meta issued for this tenant connection.')
                        }
                      />
                    </Grid>
                  </Grid>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={manualFormData.defaultConnection}
                        onChange={(event) => handleManualFieldChange('defaultConnection', event.target.checked)}
                      />
                    }
                    label={t('whatsapp.fields.defaultConnection', 'Use as default outbound connection')}
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                      variant="contained"
                      startIcon={saveConnectionMutation.isLoading ? <CircularProgress size={18} /> : <Save />}
                      onClick={handleSaveConnection}
                      disabled={!isManualDirty || saveConnectionMutation.isLoading}
                    >
                      {t('whatsapp.buttons.save', 'Save')}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={connectionCheckMutation.isLoading ? <CircularProgress size={18} /> : <SettingsInputAntenna />}
                      onClick={() =>
                        connectionCheckMutation.mutate({
                          connectionId: activeConnection?.id,
                          action: 'test',
                        })
                      }
                      disabled={connectionCheckMutation.isLoading || (!activeConnection?.id && isCreatingNewConnection)}
                    >
                      {t('whatsapp.buttons.testConnection', 'Test Connection')}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={connectionCheckMutation.isLoading ? <CircularProgress size={18} /> : <Refresh />}
                      onClick={() =>
                        connectionCheckMutation.mutate({
                          connectionId: activeConnection?.id,
                          action: 'refresh',
                        })
                      }
                      disabled={connectionCheckMutation.isLoading || (!activeConnection?.id && isCreatingNewConnection)}
                    >
                      {t('whatsapp.buttons.refreshStatus', 'Refresh Status')}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Stack spacing={3}>
            <Paper sx={cardSx}>
              <Typography variant="h6" gutterBottom>
                {t('whatsapp.sections.status.title', 'Status')}
              </Typography>

              {activeConnection ? (
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {t('whatsapp.status.connection', 'Connection')}
                    </Typography>
                    <Chip
                      label={enumLabel(t, 'whatsapp.connectionStatus', activeConnection.connectionStatus)}
                      color={connectionChipColor(activeConnection.connectionStatus)}
                      size="small"
                    />
                    {activeConnection.defaultConnection && (
                      <Chip label={t('whatsapp.common.default', 'Default')} size="small" color="success" variant="outlined" />
                    )}
                  </Stack>
                  <Typography variant="body2">
                    <strong>{t('whatsapp.status.lastApiCheck', 'Last API check:')}</strong> {formatDateTime(activeConnection.lastApiCheckAt, t)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('whatsapp.status.lastSuccessfulApiCommunication', 'Last successful API communication:')}</strong>{' '}
                    {formatDateTime(activeConnection.lastSuccessfulApiCommunicationAt, t)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('whatsapp.status.phoneNumberStatus', 'Phone number status:')}</strong> {activeConnection.phoneNumberStatus || t('whatsapp.common.unknown', 'Unknown')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('whatsapp.status.businessAccountStatus', 'Business account status:')}</strong> {activeConnection.businessAccountStatus || t('whatsapp.common.unknown', 'Unknown')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('whatsapp.status.verifiedPhoneNumber', 'Verified phone number:')}</strong> {activeConnection.verifiedPhoneNumber || t('whatsapp.common.notAvailable', 'Not available')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('whatsapp.status.verifiedName', 'Verified name:')}</strong> {activeConnection.verifiedName || activeConnection.businessDisplayName || t('whatsapp.common.notAvailable', 'Not available')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('whatsapp.status.onboardingMethod', 'Onboarding method:')}</strong> {enumLabel(t, 'whatsapp.onboardingMethod', activeConnection.onboardingMethod)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('whatsapp.status.onboardingStatus', 'Onboarding status:')}</strong> {enumLabel(t, 'whatsapp.onboardingStatusValue', activeConnection.onboardingStatus)}
                  </Typography>
                </Stack>
              ) : (
                <Alert severity="info">
                  {t('whatsapp.alerts.noActiveConnection', 'Select a connection to inspect its status.')}
                </Alert>
              )}

              {latestConnectionCheck && (
                <Alert severity={latestConnectionCheck.success ? 'success' : 'warning'} sx={{ mt: 2 }}>
                  {latestConnectionCheck.message}
                </Alert>
              )}

              {activeConnection?.lastErrorMessage && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {activeConnection.lastErrorMessage}
                </Alert>
              )}
            </Paper>

            {overview?.hasCompanyContext && (
              <Paper sx={cardSx}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <Send color="success" />
                  <Box>
                    <Typography variant="h6">{t('whatsapp.sections.testMessage.title', 'Test Message')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('whatsapp.sections.testMessage.descriptionBefore', "Send Meta's standard ")}
                      <code>hello_world</code>
                      {t('whatsapp.sections.testMessage.descriptionAfter', ' template to confirm outbound delivery from the selected phone number.')}
                    </Typography>
                  </Box>
                </Stack>

                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label={t('whatsapp.fields.destinationPhoneNumber', 'Destination Phone Number')}
                    value={destinationPhoneNumber}
                    onChange={(event) => setDestinationPhoneNumber(event.target.value)}
                    placeholder="+15551234567"
                    error={Boolean(destinationPhoneNumber) && !destinationNumberValid}
                    helperText={
                      Boolean(destinationPhoneNumber) && !destinationNumberValid
                        ? t('whatsapp.fields.destinationPhoneNumberHelperInvalid', 'Use E.164 format, for example +15551234567.')
                        : t('whatsapp.fields.destinationPhoneNumberHelperValid', 'The number must already be reachable in WhatsApp.')
                    }
                  />
                  <Box>
                    <Button
                      variant="contained"
                      startIcon={sendTestMessageMutation.isLoading ? <CircularProgress size={18} /> : <Send />}
                      onClick={handleSendTestMessage}
                      disabled={sendTestMessageMutation.isLoading || !destinationPhoneNumber.trim() || !selectedConnectionId}
                    >
                      {t('whatsapp.buttons.sendTestMessage', 'Send Test Message')}
                    </Button>
                  </Box>

                  {latestTestMessage && (
                    <Alert severity={latestTestMessage.success ? 'success' : 'error'}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {latestTestMessage.message}
                      </Typography>
                      <Typography variant="body2">
                        {t('whatsapp.testResult.messageId', 'WhatsApp Message ID: {{id}}', {
                          id: latestTestMessage.whatsappMessageId || t('whatsapp.common.notReturned', 'Not returned'),
                        })}
                      </Typography>
                      <Typography variant="body2">
                        {t('whatsapp.testResult.deliveryStatus', 'Delivery Status: {{status}}', {
                          status: enumLabel(t, 'whatsapp.deliveryStatus', latestTestMessage.deliveryStatus),
                        })}
                      </Typography>
                    </Alert>
                  )}
                </Stack>
              </Paper>
            )}

            <Paper sx={cardSx}>
              <Typography variant="h6" gutterBottom>
                {t('whatsapp.sections.checklist.title', 'Meta Checklist')}
              </Typography>
              <Stack spacing={1.25}>
                <Typography variant="body2">
                  {t('whatsapp.checklist.step1v2', '1. Create one Meta app for the platform and add the WhatsApp product.')}
                </Typography>
                <Typography variant="body2">
                  {t('whatsapp.checklist.step2v2', '2. Configure Facebook Login for Business and copy the Configuration ID into the shared platform settings.')}
                </Typography>
                <Typography variant="body2">
                  {t('whatsapp.checklist.step3v2', '3. Add the shared webhook callback URL and verify token in Meta, then subscribe to WhatsApp webhooks.')}
                </Typography>
                <Typography variant="body2">
                  {t('whatsapp.checklist.step4v2', '4. Put the app through Advanced Access / App Review for tech-provider usage before onboarding customer businesses in production.')}
                </Typography>
                <Typography variant="body2">
                  {t('whatsapp.checklist.step5v2', '5. Let each tenant connect its own number through Embedded Signup and add billing inside WhatsApp Manager.')}
                </Typography>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {overview?.hasCompanyContext && (
        <Paper sx={{ ...cardSx, mt: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6">{t('whatsapp.sections.logs.title', 'Logs')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('whatsapp.sections.logs.description', 'Newest entries appear first for outbound messages, inbound messages, and webhook events.')}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={logsQuery.isFetching ? <CircularProgress size={18} /> : <Refresh />}
              onClick={() => logsQuery.refetch()}
              disabled={logsQuery.isFetching}
            >
              {t('whatsapp.buttons.refreshLogs', 'Refresh Logs')}
            </Button>
          </Stack>

          {logsQuery.isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                  {t('whatsapp.sections.outboundMessages.title', 'Outbound Messages')}
                </Typography>
                <OutboundLogs logs={activeLogs.outboundMessages} t={t} />
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                  {t('whatsapp.sections.inboundMessages.title', 'Inbound Messages')}
                </Typography>
                <InboundLogs logs={activeLogs.inboundMessages} t={t} />
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                  {t('whatsapp.sections.webhookEvents.title', 'Webhook Events')}
                </Typography>
                <WebhookLogs logs={activeLogs.webhookEvents} t={t} />
              </Box>
            </Stack>
          )}
        </Paper>
      )}
    </Container>
  );
};
